/**
 * WeatherAlerts Component for HoneyDo Platform
 *
 * Displays real-time weather alerts that affect user's outdoor tasks.
 * Uses Flowbite Alert components with contextual actions.
 */

import React, { useState, useEffect } from 'react';
import { Alert } from 'flowbite-react';
import {
  CloudRain,
  Sun,
  Wind,
  Thermometer,
  AlertTriangle,
  X,
  ExternalLink,
  Calendar
} from 'lucide-react';
import { weatherAlertService, WeatherAlert } from '../../services/weatherAlertService';
import { Task } from '../../types/api';

interface WeatherAlertsProps {
  tasks: Task[];
  location?: {
    zipCode?: string;
    latitude?: number;
    longitude?: number;
  };
  onViewTasks?: (taskIds: string[]) => void;
  onReschedule?: (taskIds: string[]) => void;
  className?: string;
}

const WeatherAlerts: React.FC<WeatherAlertsProps> = ({
  tasks,
  location,
  onViewTasks,
  onReschedule,
  className = ''
}) => {
  const [alerts, setAlerts] = useState<WeatherAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState<string | null>(null);

  // Auto-refresh every 30 minutes
  useEffect(() => {
    checkWeatherAlerts();

    const interval = setInterval(() => {
      checkWeatherAlerts();
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(interval);
  }, [tasks, location]);

  /**
   * Check for weather alerts
   */
  const checkWeatherAlerts = async () => {
    if (!location || (!location.zipCode && (!location.latitude || !location.longitude))) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const result = await weatherAlertService.checkWeatherAlerts({
        tasks,
        location,
        forecastDays: 7
      });

      const sortedAlerts = weatherAlertService.sortAlertsByPriority(result.alerts);
      setAlerts(sortedAlerts);
      setLastCheck(result.checkTimestamp);
    } catch (error) {
      console.error('[WeatherAlerts] Failed to check weather alerts:', error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle alert dismissal
   */
  const handleDismiss = (alertId: string) => {
    weatherAlertService.dismissAlert(alertId);
    setAlerts(alerts.filter(a => a.id !== alertId));
  };

  /**
   * Handle "View Tasks" action
   */
  const handleViewTasks = (alert: WeatherAlert) => {
    if (onViewTasks) {
      onViewTasks(alert.affectedTaskIds);
    } else {
      // Default: scroll to tasks section or filter tasks
      console.log('[WeatherAlerts] View tasks:', alert.affectedTaskIds);
    }
  };

  /**
   * Handle "Reschedule" action
   */
  const handleReschedule = (alert: WeatherAlert) => {
    if (onReschedule) {
      onReschedule(alert.affectedTaskIds);
    } else {
      console.log('[WeatherAlerts] Reschedule tasks:', alert.affectedTaskIds);
    }
  };

  /**
   * Get icon component for alert
   */
  const getAlertIcon = (iconType: WeatherAlert['icon']) => {
    const iconProps = { className: "w-5 h-5" };

    switch (iconType) {
      case 'rain':
        return <CloudRain {...iconProps} />;
      case 'sun':
        return <Sun {...iconProps} />;
      case 'wind':
        return <Wind {...iconProps} />;
      case 'temperature':
        return <Thermometer {...iconProps} />;
      case 'storm':
        return <AlertTriangle {...iconProps} />;
      default:
        return <AlertTriangle {...iconProps} />;
    }
  };

  /**
   * Get Flowbite color for severity
   */
  const getSeverityColor = (severity: WeatherAlert['severity']) => {
    switch (severity) {
      case 'danger':
        return 'failure';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  };

  if (loading && alerts.length === 0) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
      </div>
    );
  }

  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Alert header with count */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Weather Alerts
          <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded-full">
            {weatherAlertService.getAlertBadgeCount(alerts)}
          </span>
        </h3>
        {lastCheck && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Updated {new Date(lastCheck).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Alert list */}
      <div className="space-y-2">
        {alerts.map((alert) => (
          <Alert
            key={alert.id}
            color={getSeverityColor(alert.severity)}
            icon={() => getAlertIcon(alert.icon)}
            onDismiss={alert.dismissible ? () => handleDismiss(alert.id) : undefined}
            className="shadow-sm"
          >
            <div className="flex flex-col gap-2">
              {/* Alert title and message */}
              <div>
                <h4 className="font-semibold text-sm">{alert.title}</h4>
                <p className="text-sm mt-1">{alert.message}</p>
              </div>

              {/* Alert actions */}
              {alert.actionable && alert.actions && alert.actions.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {alert.actions.map((action, index) => {
                    if (action.action === 'view_tasks') {
                      return (
                        <button
                          key={index}
                          onClick={() => handleViewTasks(alert)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {action.label}
                        </button>
                      );
                    }

                    if (action.action === 'reschedule') {
                      return (
                        <button
                          key={index}
                          onClick={() => handleReschedule(alert)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 rounded-lg transition-colors"
                        >
                          <Calendar className="w-3 h-3" />
                          {action.label}
                        </button>
                      );
                    }

                    return null;
                  })}
                </div>
              )}

              {/* Task count indicator */}
              {alert.affectedTaskCount > 0 && (
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Affects {alert.affectedTaskCount} task{alert.affectedTaskCount > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </Alert>
        ))}
      </div>

      {/* Refresh button */}
      <button
        onClick={checkWeatherAlerts}
        disabled={loading}
        className="w-full px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Checking weather...' : 'Refresh alerts'}
      </button>
    </div>
  );
};

export default WeatherAlerts;
